/**
 * CL-ACCESS-008A / CL-STORAGE-001A — Authenticated Storage blob retrieval (token-free).
 * Portal open/preview/download must use getBlob/getBytes after entitlement,
 * then createObjectURL; revoke when the consumer is done.
 */

import { getBlob, getBytes, ref } from "firebase/storage";
import { classifyStorageSdkError, STORAGE_ERROR_CODES } from "./projectFileStorageErrorModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

/** Bound getBlob so the UI can terminate instead of waiting on SDK retry-limit. */
export const STORAGE_BLOB_FETCH_TIMEOUT_MS = 12000;

/**
 * Large overall packages (10MB+) often exceed 12s on authenticated getBlob.
 * Originals use a longer bound; thumbnails keep the short preview timeout.
 */
export const STORAGE_ORIGINAL_BLOB_FETCH_TIMEOUT_MS = 180000;

/** Target secure object path (Design A — versioned path). */
export function buildVersionedPmProjectFileObjectPath(projectId, fileId, kind = "original") {
  const pid = safeStr(projectId);
  const fid = safeStr(fileId);
  const objectKind = safeStr(kind) === "thumbnail" ? "thumbnail" : "original";
  if (!pid || !fid) return "";
  return `pmProjectFiles/${pid}/files/${fid}/${objectKind}`;
}

function timeoutError(ms) {
  const err = new Error("preview_timeout");
  err.code = "preview_timeout";
  err.timeoutMs = ms;
  return err;
}

function withTimeout(promise, ms) {
  const limit = Number(ms);
  if (!Number.isFinite(limit) || limit <= 0) return promise;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(timeoutError(limit)), limit);
    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

/**
 * Fetch file bytes via authenticated SDK (no download token URL).
 * Does not retry after the first attempt terminates (timeout or SDK rejection).
 * @returns {{ ok: true, blob: Blob, objectUrl: string, path: string } | { ok: false, errorCode: string, error: string }}
 */
export async function fetchAuthorizedProjectFileBlob({
  storage,
  path = "",
  timeoutMs = STORAGE_BLOB_FETCH_TIMEOUT_MS,
  getBlobImpl = getBlob,
  refImpl = ref,
  createObjectURL = typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
    ? URL.createObjectURL.bind(URL)
    : null,
} = {}) {
  const objectPath = safeStr(path);
  if (!storage || !objectPath) {
    return { ok: false, errorCode: STORAGE_ERROR_CODES.UNAVAILABLE, error: "Missing storage or path." };
  }
  if (typeof getBlobImpl !== "function" || typeof refImpl !== "function") {
    return { ok: false, errorCode: STORAGE_ERROR_CODES.UNAVAILABLE, error: "Storage getBlob is unavailable." };
  }
  try {
    const blob = await withTimeout(getBlobImpl(refImpl(storage, objectPath)), timeoutMs);
    if (!createObjectURL) {
      return { ok: true, blob, objectUrl: "", path: objectPath };
    }
    const objectUrl = createObjectURL(blob);
    return { ok: true, blob, objectUrl, path: objectPath };
  } catch (e) {
    const errorCode = classifyStorageSdkError(e);
    return { ok: false, errorCode, error: "Authorized blob fetch failed." };
  }
}

export async function fetchAuthorizedProjectFileBytes({
  storage,
  path = "",
  timeoutMs = STORAGE_BLOB_FETCH_TIMEOUT_MS,
  getBytesImpl = getBytes,
  refImpl = ref,
} = {}) {
  const objectPath = safeStr(path);
  if (!storage || !objectPath) {
    return { ok: false, errorCode: STORAGE_ERROR_CODES.UNAVAILABLE, error: "Missing storage or path." };
  }
  try {
    const bytes = await withTimeout(getBytesImpl(refImpl(storage, objectPath)), timeoutMs);
    return { ok: true, bytes, path: objectPath };
  } catch (e) {
    const errorCode = classifyStorageSdkError(e);
    return { ok: false, errorCode, error: "Authorized bytes fetch failed." };
  }
}

export function revokeTemporaryObjectUrl(objectUrl, revoke = typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function" ? URL.revokeObjectURL.bind(URL) : null) {
  const href = safeStr(objectUrl);
  if (!href || typeof revoke !== "function") return;
  try {
    revoke(href);
  } catch {
    // ignore
  }
}
